/*
 * Minimal markdown-to-HTML for the subset models emit: paragraphs, headings, lists,
 * fenced code, tables, and inline code/bold/em/links. Input is HTML-escaped up front,
 * so the output contains only tags these functions generate — safe to inject via
 * dangerouslySetInnerHTML without a sanitizer dependency.
 */

import { isInternalLink } from "./entityRoutes";

function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// Inline markdown on already-escaped text; only produces a fixed set of safe tags.
// Relative links are kept only for whitelisted internal pages (see entityRoutes).
function inline(text: string): string {
  return text
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/\[([^\]]+)\]\((https?:[^)\s]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\[([^\]]+)\]\((\/[^)\s]+)\)/g, (match, label, url) => (isInternalLink(url) ? `<a href="${url}" data-internal-link>${label}</a>` : match));
}

export function renderMarkdown(markdown: string): string {
  const lines = escapeHtml(markdown).split("\n");
  const html: string[] = [];
  let paragraph: string[] = [];
  let list: "ul" | "ol" | null = null;
  let code: string[] | null = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      html.push("<p>" + paragraph.map(inline).join("<br>") + "</p>");
      paragraph = [];
    }
  };
  const closeList = () => {
    if (list) {
      html.push("</" + list + ">");
      list = null;
    }
  };
  const parseRow = (row: string) =>
    row
      .replace(/^\s*\|/, "")
      .replace(/\|\s*$/, "")
      .split("|")
      .map((cell) => cell.trim());

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (code !== null) {
      if (/^```/.test(line)) {
        html.push("<pre><code>" + code.join("\n") + "</code></pre>");
        code = null;
      } else {
        code.push(line);
      }
      continue;
    }
    if (/^```/.test(line)) {
      flushParagraph();
      closeList();
      code = [];
      continue;
    }
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      flushParagraph();
      closeList();
      const level = heading[1].length + 2;
      html.push("<h" + level + ">" + inline(heading[2]) + "</h" + level + ">");
      continue;
    }
    const separatorNext = i + 1 < lines.length && /^\s*\|?[\s:|-]+$/.test(lines[i + 1]) && lines[i + 1].includes("-") && lines[i + 1].includes("|");
    if (line.includes("|") && separatorNext) {
      flushParagraph();
      closeList();
      let table =
        "<table><thead><tr>" +
        parseRow(line)
          .map((cell) => "<th>" + inline(cell) + "</th>")
          .join("") +
        "</tr></thead><tbody>";
      i += 2;
      for (; i < lines.length && lines[i].includes("|"); i++) {
        table +=
          "<tr>" +
          parseRow(lines[i])
            .map((cell) => "<td>" + inline(cell) + "</td>")
            .join("") +
          "</tr>";
      }
      i--;
      html.push(table + "</tbody></table>");
      continue;
    }
    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+\.\s+(.*)$/);
    if (bullet || numbered) {
      flushParagraph();
      const type = bullet ? "ul" : "ol";
      if (list !== type) {
        closeList();
        html.push("<" + type + ">");
        list = type;
      }
      html.push("<li>" + inline((bullet || numbered)![1]) + "</li>");
      continue;
    }
    if (!line.trim()) {
      flushParagraph();
      closeList();
      continue;
    }
    paragraph.push(line);
  }
  if (code !== null) {
    html.push("<pre><code>" + code.join("\n") + "</code></pre>");
  }
  flushParagraph();
  closeList();
  return html.join("");
}
